import React, { useEffect, useState } from 'react';
import { ServerInterface } from "src/store/mine_craft/serversSlice";
import { updateServerHostedConfig } from "src/actions/mine_craft/serversActions";
import { useAppDispatch } from "src/store/hooks";

import { InputNumber, Modal, Radio, message } from 'antd';

type PowerMode = 'afk' | 'always_on';

// crafty-servers echoes its config back in camelCase; rows provisioned
// before power modes existed carry neither key, and then the provider
// default (afk) is what actually runs.
function currentPower(config: { [key: string]: any } | undefined) {
  const mode: PowerMode = config?.powerMode === 'always_on' ? 'always_on' : 'afk';
  const minutes = config?.autoStopMinutes;

  return { mode, minutes: typeof minutes === 'number' ? minutes : null };
}

const _PowerModeForm = ({ server, open, onClose }: {
  server: ServerInterface,
  open: boolean,
  onClose: () => void
}) => {
  const dispatch = useAppDispatch();
  const config = server.hosted_server?.config;

  const [mode, setMode] = useState<PowerMode>('afk');
  const [minutes, setMinutes] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  // Re-seeded on every open: a sync or webhook may have refreshed the
  // echoed config since the last edit.
  useEffect(() => {
    if (!open) return;

    const current = currentPower(config);
    setMode(current.mode);
    setMinutes(current.minutes);
  }, [open, config]);

  async function apply() {
    const data: any = { power_mode: mode };
    if (mode === 'afk' && minutes !== null) data.auto_stop_minutes = minutes;

    setBusy(true);
    const resultAction = await dispatch(updateServerHostedConfig({ id: server.id, data }));
    setBusy(false);

    if (updateServerHostedConfig.fulfilled.match(resultAction)) {
      message.success('Power mode saved');
      onClose();
    } else {
      const payload: any = resultAction.payload;
      message.error(Array.isArray(payload) ? payload[0] : 'Power mode could not be saved');
    }
  }

  return (
    <Modal
      title='Power mode'
      open={open}
      onCancel={onClose}
      onOk={apply}
      okText='Save'
      okButtonProps={{ loading: busy }}
      destroyOnClose
    >
      <div className='v-srv-power'>
        <div className='o-field'>
          <label>Mode</label>
          <Radio.Group
            optionType='button'
            buttonStyle='solid'
            value={mode}
            onChange={event => setMode(event.target.value)}
          >
            <Radio value='afk'>Sleep when idle</Radio>
            <Radio value='always_on'>Always on</Radio>
          </Radio.Group>
        </div>

        {mode === 'afk' &&
          <div className='o-field'>
            <label>Sleep after (minutes without players)</label>
            <InputNumber
              min={0}
              max={1440}
              value={minutes}
              onChange={setMinutes}
              placeholder='provider default'
            />
          </div>
        }

        <div className='o-hint'>
          Sleep when idle stops the server once nobody has been on for the window
          above and wakes it on the first join — it stays in the players list with
          a sleeping MOTD meanwhile. Always on never sleeps and restarts itself
          after a crash.
        </div>
      </div>
    </Modal>
  );
}

export default _PowerModeForm;
